"use client";

import { Link } from "next-view-transitions";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "~/components/ui/navigation-menu";
import { usePathname } from "~/hooks/use-pathname";
import { cn } from "~/lib/utils";
import type { ResolvedNavGroup } from "./nav-resolver";

type Props = {
  groups: ResolvedNavGroup[];
  className?: string;
};

export function DesktopNav({ groups, className }: Props) {
  const pathname = usePathname();

  return (
    <NavigationMenu viewport={false} className={className}>
      <NavigationMenuList className="gap-0.5 xl:gap-1">
        {groups.map((group) => {
          const isGroupActive = group.items.some(
            (item) => item.href === pathname,
          );
          const single = group.items.length === 1 ? group.items[0] : undefined;
          if (single) {
            // One destination: render a direct link styled like a trigger
            return (
              <NavigationMenuItem key={group.key}>
                <NavigationMenuLink asChild active={isGroupActive}>
                  <Link
                    prefetch
                    href={single.href}
                    className={cn(
                      "inline-flex h-10 w-max items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors hover:bg-muted/60 hover:text-primary focus-visible:bg-muted/60 focus:outline-none xl:px-3.5",
                      isGroupActive && "text-primary",
                    )}
                  >
                    {group.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            );
          }
          return (
            <NavigationMenuItem key={group.key}>
              <NavigationMenuTrigger
                className={cn(
                  "h-10 rounded-lg bg-transparent px-2 text-sm font-medium hover:bg-muted/60 hover:text-primary focus-visible:bg-muted/60 data-[active]:bg-transparent data-[state=open]:bg-muted/60 data-[state=open]:text-primary xl:px-3.5",
                  isGroupActive && "text-primary",
                )}
              >
                {group.label}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="min-w-56 p-1.5">
                  <ul className="grid gap-0.5">
                    {group.items.map((item) => {
                      const active = pathname === item.href;
                      return (
                        <li key={item.labelKey}>
                          <NavigationMenuLink asChild active={active}>
                            <Link
                              prefetch
                              href={item.href}
                              className={cn(
                                "block whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground/80 transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground",
                                active &&
                                  "bg-primary/10 font-medium text-primary",
                              )}
                            >
                              {item.label}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
